<template>
  <!-- Valor editable al hacer clic: muestra el valor (con lapiz/chevron al hover) y, al clic, un control con foco. -->
  <span class="mf" :class="{ 'mf-block': block, editing }">
    <!-- Combo con opción "Otros" (texto libre al final de la lista). El cierre se gestiona con
         focusout en el contenedor: si el foco va del select al texto (o viceversa) NO se cierra. -->
    <span v-if="editing && options && allowOther" class="mf-combo" @focusout="onComboFocusOut">
      <select ref="ctrl" class="mf-input" :value="comboSelectValue" @change="onComboSelect($event.target.value)">
        <option v-for="o in options" :key="o.value" :value="o.value">{{ o.label }}</option>
      </select>
      <input v-if="otherMode" ref="other" type="text" class="mf-input mf-other" :value="draft"
             @input="draft = $event.target.value" @keydown.enter.prevent="commitOther" @keydown.esc="cancel">
    </span>
    <select v-else-if="editing && options" ref="ctrl" class="mf-input" :value="value" @change="onSelect($event.target.value)" @blur="editing = false">
      <option v-for="o in options" :key="o.value" :value="o.value">{{ o.label }}</option>
    </select>
    <input v-else-if="editing && type === 'date'" ref="ctrl" type="date" class="mf-input" :value="value"
           @change="onDate($event.target.value)" @blur="editing = false" @keydown.esc="cancel">
    <input v-else-if="editing" ref="ctrl" class="mf-input" :value="draft"
           @input="onInput($event.target.value)" @blur="commit" @keydown.enter.prevent="commit" @keydown.esc="cancel">
    <span v-else class="mf-view" :class="{ 'is-select': !!options }" @click="edit">
      <!-- Nivel apilado: numero arriba, etiqueta debajo (impacto / probabilidad) -->
      <span v-if="sub" class="mf-stack">
        <span class="mf-main">{{ isEmpty ? '' : value }}</span>
        <span class="mf-sub">{{ isEmpty ? '' : sub(value) }}</span>
      </span>
      <!-- Valor normal (texto/fecha/tipo/estado). Vacio: nbsp para mantener la linea del punteado. -->
      <span v-else class="mf-text" :class="[{ underline: !options, empty: isEmpty }, viewClass]">{{ shown }}</span>
      <i v-if="options" class="fas fa-chevron-down mf-chevron"></i>
      <i v-else class="fas fa-pencil mf-pencil"></i>
    </span>
  </span>
</template>

<script>
  import { fmtDate } from '../shared/format'

  // Valor de la opción "Otros" del combo (texto libre). Debe coincidir con el que ponga el padre.
  const OTHER_VALUE = '__other__'

  export default {
    name: 'PrmSummaryManualField',

    props: {
      value: { type: [String, Number], default: '' },
      type: { type: String, default: 'text' },   // text | date
      options: { type: Array, default: null },    // lista cerrada (select)
      allowOther: { type: Boolean, default: false }, // select con "Otros" -> texto libre
      sub: { type: Function, default: null },     // etiqueta secundaria (nivel apilado)
      format: { type: Function, default: null },  // formateo de solo lectura (p. ej. €)
      viewClass: { type: [String, Array], default: '' }, // estilo del valor (color/badge)
      block: { type: Boolean, default: false },   // ocupa el ancho de la celda al editar
    },

    data () {
      return { editing: false, draft: '', otherMode: false }
    },

    computed: {
      isEmpty () {
        return this.value === '' || this.value === null || this.value === undefined
      },
      // Valor custom: hay opciones pero el valor actual no está en el catálogo (lo escribió a mano).
      isCustom () {
        return !!this.options && !this.isEmpty && !this.options.some((o) => String(o.value) === String(this.value))
      },
      // Qué marca el select del combo: "Otros" si estamos en modo texto o el valor es custom.
      comboSelectValue () {
        return (this.otherMode || this.isCustom) ? OTHER_VALUE : this.value
      },
      display () {
        if (this.isEmpty) return ''
        if (this.options) {
          const opt = this.options.find((o) => String(o.value) === String(this.value))
          return opt ? opt.label : this.value
        }
        if (this.type === 'date') return fmtDate(this.value)
        return this.format ? this.format(this.value) : this.value
      },
      // Valor mostrado; si está vacío usamos un nbsp para conservar la línea de texto.
      shown () {
        return this.isEmpty ? String.fromCharCode(160) : this.display
      },
    },

    methods: {
      edit () {
        this.draft = this.isEmpty ? '' : String(this.value)
        this.otherMode = this.allowOther && this.isCustom
        this.editing = true
        this.$nextTick(() => {
          // Valor custom (Otros): enfocamos directamente el texto libre.
          if (this.otherMode && this.$refs.other) { this.$refs.other.focus(); return }
          const el = this.$refs.ctrl
          if (!el) return
          el.focus()
          // Selects y fechas: abrimos el desplegable al primer clic (si el navegador lo soporta).
          if (typeof el.showPicker === 'function') {
            try { el.showPicker() } catch (e) { /* sin gesto/soporte: queda enfocado */ }
          }
        })
      },
      // Emitimos el valor en vivo mientras se escribe (para vistas que reaccionan al instante, p. ej. una barra).
      onInput (v) {
        this.draft = v
        this.$emit('input', v)
      },
      commit () {
        this.editing = false
        if (String(this.draft) !== String(this.isEmpty ? '' : this.value)) {
          this.$emit('save', this.draft)
        }
      },
      // Al cancelar reemitimos el valor confirmado, para que cualquier vista en vivo (p. ej. una barra) vuelva a él.
      cancel () {
        this.editing = false
        this.$emit('input', this.isEmpty ? '' : this.value)
      },
      onSelect (v) {
        this.editing = false
        this.$emit('save', v)
      },
      onDate (v) {
        this.editing = false
        this.$emit('save', v)
      },
      // Combo "Otros": elegir una opción del catálogo guarda; elegir "Otros" abre el texto libre.
      onComboSelect (v) {
        if (v === OTHER_VALUE) {
          this.otherMode = true
          this.draft = this.isCustom ? String(this.value) : ''
          this.$nextTick(() => { if (this.$refs.other) this.$refs.other.focus() })
          return
        }
        this.otherMode = false
        this.editing = false
        this.$emit('save', v)
      },
      // Enter en el texto de "Otros": guarda y cierra.
      commitOther () {
        this.editing = false
        this.otherMode = false
        const cur = this.isEmpty ? '' : String(this.value)
        if (String(this.draft) !== cur) this.$emit('save', this.draft)
      },
      // Foco saliendo del combo. Si sigue dentro (select <-> texto) no hacemos nada; si sale
      // del todo, guardamos el texto de "Otros" (si lo había) y cerramos.
      onComboFocusOut (e) {
        const to = e && e.relatedTarget
        if (to && this.$el.contains(to)) {
          return
        }
        if (this.otherMode) {
          const cur = this.isEmpty ? '' : String(this.value)
          if (String(this.draft) !== cur) this.$emit('save', this.draft)
        }
        this.editing = false
        this.otherMode = false
      },
    },
  }
</script>

<style lang="scss" scoped>
  // OJO: la clase NO puede llamarse "block" (colisiona con Bulma .block:not(:last-child){margin-bottom}
  // que descoloca el chip de fuente). Usamos "mf-block". Ver cerebro: colisiones de clase Tailwind/Bulma.
  .mf { display: inline-flex; align-items: center; min-width: 0; max-width: 100%; }
  .mf.mf-block.editing { display: flex; width: 100%; }

  .mf-view { display: inline-flex; align-items: center; gap: 4px; cursor: text; min-width: 0; text-decoration: none; }
  // Selectores: cursor de puntero y caja del valor con ancho reservado (el chevron no salta con/sin valor).
  .mf-view.is-select { cursor: pointer; }
  .mf-view.is-select .mf-text:not(.st-badge),
  .mf-view.is-select .mf-stack {
    min-width: 62px; min-height: 20px; padding: 1px 6px;
    border: 1px solid #d5dbe4; border-radius: 4px; background: #fff;
    display: inline-flex; align-items: center; justify-content: center;
  }
  .mf-view.is-select .mf-stack { flex-direction: column; min-height: 22px; }
  .mf-view.is-select:hover .mf-text:not(.st-badge),
  .mf-view.is-select:hover .mf-stack { border-color: #e0b52a; }
  // Ningún text-decoration heredado en el valor (evita subrayados ajenos al punteado).
  .mf-view, .mf-text { text-decoration: none; }
  .mf-view:hover, .mf-view:hover .mf-text { text-decoration: none; }
  // Subrayado punteado ambar SIEMPRE en editables, como una sola linea inferior (inline-block
  // para no subrayar cada renglon); se adapta al ancho y, vacio, guarda uno minimo.
  .mf-text.underline {
    display: inline-block; box-sizing: border-box; min-height: 15px;
    border-bottom: 1px dashed #d8c27a;
  }
  .mf-text.underline.empty { min-width: 90px; }

  // Nivel apilado (impacto / probabilidad).
  .mf-stack { display: inline-flex; flex-direction: column; align-items: center; line-height: 1.15; }
  .mf-main { font-weight: 700; font-size: 12px; }
  .mf-sub { font-size: 8px; text-transform: uppercase; letter-spacing: .02em; color: #5a7896; }

  // Tipo (riesgo rojo / oportunidad verde).
  .mf-text.type-threat { color: #c0392b; font-weight: 700; text-transform: uppercase; }
  .mf-text.type-opportunity { color: #1e8449; font-weight: 700; text-transform: uppercase; }
  // Sí / No (documentación S04).
  .mf-text.yn-yes { color: #1e8449; font-weight: 700; }
  .mf-text.yn-no { color: #c0392b; font-weight: 700; }
  // Badge de estado.
  .mf-text.st-badge { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .02em; }
  .mf-text.st-open { background: #fdecea; color: #c0392b; }
  .mf-text.st-in_progress { background: #fdf3d7; color: #b7791f; }
  .mf-text.st-closed { background: #e7f6ec; color: #1e8449; }
  // Estados de fase (Safety MSR): no iniciado (gris) / completado (verde).
  .mf-text.st-not_started { background: #eef0f3; color: #6b7280; }
  .mf-text.st-completed { background: #e7f6ec; color: #1e8449; }
  // Valor coloreado por significado (KPIs manuales): verde / ámbar / rojo.
  .mf-text.c-green { color: #1e8449; font-weight: 700; }
  .mf-text.c-amber { color: #b9770e; font-weight: 700; }
  .mf-text.c-red { color: #c0392b; font-weight: 700; }
  // Clasificación de incidencia (badge): leve (gris) / grave (ámbar) / muy grave (rojo).
  .mf-text.cl-minor { background: #eef0f3; color: #6b7280; }
  .mf-text.cl-serious { background: #fdf3d7; color: #b7791f; }
  .mf-text.cl-very_serious { background: #fdecea; color: #c0392b; }
  // Bloqueo (badge): sí (rojo) / no (gris).
  .mf-text.bl-yes { background: #fdecea; color: #c0392b; }
  .mf-text.bl-no { background: #eef0f3; color: #6b7280; }

  .mf-pencil { font-size: 10px; color: #b7791f; opacity: 0; transition: opacity .1s; flex-shrink: 0; }
  .mf-view:hover .mf-pencil { opacity: 1; }
  .mf-chevron { font-size: 9px; color: #7d94ad; opacity: .5; transition: opacity .1s; flex-shrink: 0; }
  .mf-view:hover .mf-chevron { opacity: 1; }

  // Estilo de foco: caja ámbar redondeada (como el diseño).
  .mf-input {
    min-width: 120px; font-size: 11px; color: #2c3a4d; outline: none; padding: 2px 6px;
    border: 1px solid #e0b52a; border-radius: 4px; background: #fffdf5;
    box-shadow: 0 0 0 2px rgba(224, 181, 42, .12);
  }
  .mf.mf-block.editing .mf-input { width: 100%; }

  // Combo "Otros": select del catálogo + input de texto libre, uno junto al otro.
  .mf-combo { display: inline-flex; align-items: center; gap: 4px; flex-wrap: wrap; }
  .mf-other { min-width: 150px; }
</style>
